<template>
  <CheckPermission
    :container-class="containerClass"
    :permission="permission"
  >
    <input
      ref="file"
      type="file"
      multiple
      :accept="accept"
      class="d-none"
      @change="uploadFiles($event)"
    />
    <b-button
      v-if="typeButton !== 'small'"
      :variant="color"
      :class="buttonClass"
      @click="$refs.file.click()"
      :disabled="progress.enabled || disabled"
    >
      <slot>{{ text }}</slot>
    </b-button>
    <b-button
      v-if="typeButton === 'small'"
      :variant="color"
      :class="buttonClass"
      @click="$refs.file.click()"
      :disabled="progress.enabled || disabled"
      size="sm"
    >
      <feather-icon
        v-b-tooltip.hover.left="'Adjuntar documentos'"
        icon="PaperclipIcon" />
    </b-button>
    <vs-progress
      v-if="progress.enabled"
      :percent="progress.percent"
      :indeterminate="progress.indeterminate"
      :color="color"
    />
  </CheckPermission>
</template>
<script>

import { uuid } from 'vue-uuid';
import { BButton } from 'bootstrap-vue';

import CheckPermission from '@/febos/global/usuario/components/CheckPermission';
import { ioGetPrivateUploadUrl, ioUploadFileToPrivateUrl } from '@/febos/servicios/api/herramientas.api';

const paths = {
  ed: `febos-io/chile/escritorio-digital/${process.env.VUE_APP_AMBIENTE}/archivos/expedientes/`,
  saval: `febos-io/chile/saval-demo/${process.env.VUE_APP_AMBIENTE}/archivos/`
};

export default {
  data: () => ({
    progress: {
      enabled: false,
      percent: 0,
      indeterminate: true
    }
  }),
  methods: {
    getFileExtension(file) {
      const lastDot = file.name.lastIndexOf('.');
      if (lastDot === -1) {
        return '';
      }
      return file.name.substring(lastDot + 1);
    },
    updateProgress(currentFileIndex, totalFiles, filePercent) {
      const completed = currentFileIndex / totalFiles;
      const current = (filePercent / 100) / totalFiles;
      this.progress.percent = Math.round((completed + current) * 100);
    },
    showFileSizeError(message) {
      this.$bvToast.toast(
        message,
        {
          title: '¡Oye!',
          variant: 'danger',
          pauseOnHover: true,
          solid: true,
          noAutoHide: true
        }
      );
    },
    validateFile(file) {
      const fileExtension = this.getFileExtension(file);
      if (file.size > this.maxSizeInMb * 1048576 && fileExtension !== 'rar' && fileExtension !== 'zip') {
        return `El archivo ${file.name} supera el máximo de tamaño permitido por archivo (${this.maxSizeInMb} MB)`;
      }
      return '';
    },
    resetInput() {
      if (this.$refs.file) {
        this.$refs.file.value = '';
      }
    },
    resetProgress() {
      this.progress.enabled = false;
      this.progress.percent = 0;
      this.progress.indeterminate = false;
    },
    async uploadFiles(event) {
      const files = Array.from(event.target.files || []);

      if (!files.length) {
        return false;
      }

      const totalFiles = files.length;
      const uploadedFiles = [];

      this.progress.enabled = true;
      this.progress.indeterminate = totalFiles > 1;
      this.progress.percent = 0;

      for (let index = 0; index < totalFiles; index += 1) {
        const file = files[index];
        const validationMessage = this.validateFile(file);

        if (validationMessage) {
          this.showFileSizeError(validationMessage);
          this.$emit('upload-error', {
            failed: true,
            failedMessage: validationMessage,
            name: file.name
          });
          this.updateProgress(index + 1, totalFiles, 0);
          continue;
        }

        try {
          const uploadedFile = await this.uploadSingleFile(file, index, totalFiles);
          uploadedFiles.push(uploadedFile);
        } catch (e) {
          console.error(e);
          this.$emit('upload-error', {
            failed: true,
            failedMessage: e.message,
            name: file.name
          });
          this.updateProgress(index + 1, totalFiles, 0);
        }
      }

      if (uploadedFiles.length) {
        const currentValue = Array.isArray(this.value)
          ? this.value
          : (this.value ? [this.value] : []);
        this.$emit('input', [
          ...currentValue,
          ...uploadedFiles
        ]);
      }

      this.resetInput();
      this.resetProgress();

      return true;
    },
    async uploadSingleFile(file, currentFileIndex, totalFiles) {
      const fileId = uuid.v1();
      const baseUrl = paths[this.uploadTo] || this.uploadTo;
      const identifier = this.identifier ? `-${this.identifier}` : '';
      const url = `${baseUrl}${Date.now()}${fileId}${identifier}.${file.name.split('.').pop()}`;

      this.progress.indeterminate = false;

      const response = await ioGetPrivateUploadUrl(url, file.type);

      await ioUploadFileToPrivateUrl(
        response.data?.url,
        file,
        (percent) => {
          this.updateProgress(currentFileIndex, totalFiles, percent);
        }
      );

      let fileSha = null;
      try {
        if (this.withSha) {
          fileSha = await this.getFileSHA(file);
        }
      } catch (e) {
        console.error(e);
      }

      this.updateProgress(currentFileIndex + 1, totalFiles, 0);

      return {
        id: fileId,
        name: file.name,
        currentDescription: file.name,
        mime: file.type,
        date: this.$moment().format('YYYY-MM-DD HH:MM:ss'),
        path: url,
        failed: false,
        ...(this.withSha && fileSha
          ? { sha: fileSha }
          : {}
        )
      };
    },
    async getFileSHA(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (event) => {
          try {
            const arrayBuffer = event.target.result;
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hash = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
            resolve(hash);
          } catch (error) {
            reject(new Error('Error calculando el hash'));
          }
        };

        reader.onerror = () => {
          reject(new Error('Error leyendo el archivo'));
        };

        reader.readAsArrayBuffer(file);
      });
    }

  },
  props: {
    permission: {
      type: String,
      required: true
    },
    text: {
      type: String,
    },
    accept: {
      type: String
    },
    value: {
      type: [Array, Object],
      default: () => []
    },
    uploadTo: {
      type: String,
      required: true
    },
    maxSizeInMb: {
      type: Number,
      default: 20
    },
    identifier: {
      type: String
    },
    color: {
      type: String,
      default: 'primary'
    },
    buttonClass: {
      type: [String, Object]
    },
    containerClass: {
      type: [String, Object]
    },
    typeButton: {
      type: String
    },
    withSha: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CheckPermission,
    BButton
  }
};

</script>
